// src/components/CaseView/DenialCaptureModal.tsx
// ──────────────────────────────────────────────────────────────
// Modal that appears when a physician marks a case as "Denied".
// Captures the denial reason and optional denial code, then
// updates the case. This bridges into appeal letter generation
// and P2P prep.
// ──────────────────────────────────────────────────────────────

import { useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";

// ── Types ────────────────────────────────────────────────────

interface DenialCaptureModalProps {
  caseId: string;
  onDenialSaved: (denialReason: string, denialCode?: string) => void;
  onClose: () => void;
}

// Common denial reasons — matches NewCase.tsx denial reasons
const DENIAL_REASONS = [
  "Not medically necessary",
  "Does not meet clinical criteria",
  "Step therapy required",
  "Out of network",
  "Experimental/investigational",
  "Site of care / Facility denial",
  "Insufficient documentation",
  "Level of Care",
  "Duplicate service",
  "Benefit exclusion",
  "Frequency limit exceeded",
  "Prior authorization not obtained",
];

// ── Component ────────────────────────────────────────────────

export function DenialCaptureModal({
  caseId,
  onDenialSaved,
  onClose,
}: DenialCaptureModalProps) {
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [denialCode, setDenialCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const effectiveReason = selectedReason === "other"
    ? customReason.trim()
    : selectedReason;

  const handleSave = useCallback(async () => {
    if (!effectiveReason) return;

    setSaving(true);
    setError("");

    try {
      const { error: updateError } = await supabase
        .from("cases")
        .update({
          case_status: "denied",
          denial_reason: effectiveReason,
          denial_code: denialCode.trim() || null,
          decision_at: new Date().toISOString(),
        })
        .eq("id", caseId);

      if (updateError) throw updateError;

      onDenialSaved(effectiveReason, denialCode.trim() || undefined);
    } catch (e: any) {
      console.error("[DenialCapture] Save failed:", e);
      setError(e.message || "Failed to save denial");
      setSaving(false);
    }
  }, [caseId, effectiveReason, denialCode, onDenialSaved]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-red-50 border-b border-red-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-red-900">
                Authorization Denied
              </h3>
              <p className="text-xs text-red-600 mt-0.5">
                Capture the denial reason to unlock appeal tools
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Denial reason selection */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
              Denial Reason
            </label>
            <div className="flex flex-wrap gap-2">
              {DENIAL_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => {
                    setSelectedReason(reason);
                    setCustomReason("");
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedReason === reason
                      ? "bg-red-500 text-white shadow-sm"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {reason}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelectedReason("other")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedReason === "other"
                    ? "bg-red-500 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Other
              </button>
            </div>
          </div>

          {/* Custom reason input */}
          {selectedReason === "other" && (
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">
                Describe the denial reason
              </label>
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="e.g., Peer-to-peer not completed in time"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                autoFocus
              />
            </div>
          )}

          {/* Denial code (optional) */}
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">
              Denial Code{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={denialCode}
              onChange={(e) => setDenialCode(e.target.value)}
              placeholder="e.g., PR-204, N11"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* What happens next */}
          {effectiveReason && (
            <div className="px-3 py-2.5 rounded-lg bg-teal-50 border border-teal-200">
              <p className="text-xs text-teal-800 font-medium mb-1">
                After saving, you'll be able to:
              </p>
              <ul className="space-y-0.5">
                <li className="text-xs text-teal-700 flex items-center gap-1.5">
                  <span className="text-teal-400">→</span>
                  Generate an appeal letter with point-by-point rebuttal
                </li>
                <li className="text-xs text-teal-700 flex items-center gap-1.5">
                  <span className="text-teal-400">→</span>
                  Arm Call Shield for peer-to-peer review
                </li>
                <li className="text-xs text-teal-700 flex items-center gap-1.5">
                  <span className="text-teal-400">→</span>
                  Access P2P prep with denial-specific rebuttals
                </li>
              </ul>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-600">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!effectiveReason || saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Mark as Denied"}
          </button>
        </div>
      </div>
    </div>
  );
}